export const PLAN_IDS = ['free', 'starter', 'pro', 'business'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

/** `null` means unlimited. */
export interface PlanLimits {
  rendersPerMonth: number;
  templates: number | null;
  members: number | null;
  automations: number | null;
  /** Max renders in a single batch / automation run. */
  maxBatchSize: number;
  /** Public API requests per minute per API key. */
  apiRateLimitPerMinute: number;
  maxScale: number;
  storageMb: number;
  /** AI requests per month (one design request returns several variants). */
  aiCredits: number;
  /** Own canvas sizes saved in the workspace (formats of niche platforms, screens…). */
  customSizes: number | null;
  /** Brand kits (colours, fonts, logos templates refer to). */
  brands: number | null;
  /**
   * Seconds of video per month, counted by the length of the finished clip. A clip rendered on
   * the user's computer takes BROWSER_VIDEO_COST of what the same clip costs on our servers.
   */
  videoSecondsPerMonth: number;
}

/** Share of the video allowance a second rendered on the user's computer uses (we pay no CPU). */
export const BROWSER_VIDEO_COST = 0.25;

/** What a clip of `durationMs` takes from the video allowance, in ms. */
export function videoCostMs(durationMs: number, browser: boolean): number {
  return Math.round(Math.max(0, durationMs) * (browser ? BROWSER_VIDEO_COST : 1));
}

export interface PlanFeatures {
  api: boolean;
  customFonts: boolean;
  /** Size variants linked to a master template that follow its changes. */
  linkedSizes: boolean;
  integrations: boolean;
  scheduling: boolean;
  signedUrls: boolean;
  /** QR codes with rounded / dotted modules, styled corners, a colour of their own and a logo. */
  qrStyles: boolean;
  /** Template history with rollback, comments on templates, the activity log. */
  versionHistory: boolean;
  comments: boolean;
  activityLog: boolean;
  /** Template approval: review statuses, and production renders drawing the approved state. */
  approvals: boolean;
  pdf: boolean;
  prioritySupport: boolean;
  /** Videos drawn and encoded in the user's browser (experimental): a quarter of the video cost. */
  browserRender: boolean;
}

export interface Plan {
  id: PlanId;
  name: string;
  limits: PlanLimits;
  features: PlanFeatures;
  highlighted?: boolean;
}

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: 'free',
    name: 'Free',
    limits: {
      rendersPerMonth: 50,
      templates: 3,
      members: 1,
      automations: 1,
      maxBatchSize: 25,
      apiRateLimitPerMinute: 20,
      maxScale: 1,
      storageMb: 100,
      aiCredits: 5,
      customSizes: 0,
      brands: 0,
      videoSecondsPerMonth: 120,
    },
    features: {
      api: true,
      customFonts: false,
      linkedSizes: false,
      integrations: false,
      scheduling: false,
      signedUrls: false,
      qrStyles: false,
      versionHistory: false,
      comments: false,
      activityLog: false,
      approvals: false,
      pdf: true,
      browserRender: true,
      prioritySupport: false,
    },
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    limits: {
      rendersPerMonth: 1000,
      templates: 25,
      members: 3,
      automations: 5,
      maxBatchSize: 250,
      apiRateLimitPerMinute: 60,
      maxScale: 2,
      storageMb: 2000,
      aiCredits: 100,
      customSizes: 10,
      brands: 0,
      videoSecondsPerMonth: 1200,
    },
    features: {
      api: true,
      customFonts: false,
      linkedSizes: false,
      integrations: true,
      scheduling: false,
      signedUrls: true,
      qrStyles: false,
      versionHistory: false,
      comments: false,
      activityLog: false,
      approvals: false,
      pdf: true,
      browserRender: true,
      prioritySupport: false,
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    highlighted: true,
    limits: {
      rendersPerMonth: 5000,
      templates: null,
      members: 10,
      automations: 30,
      maxBatchSize: 1000,
      apiRateLimitPerMinute: 120,
      maxScale: 3,
      storageMb: 10000,
      aiCredits: 500,
      customSizes: 50,
      brands: 1,
      videoSecondsPerMonth: 6000,
    },
    features: {
      api: true,
      customFonts: true,
      linkedSizes: true,
      integrations: true,
      scheduling: true,
      signedUrls: true,
      qrStyles: true,
      versionHistory: false,
      comments: false,
      activityLog: false,
      approvals: false,
      pdf: true,
      browserRender: true,
      prioritySupport: false,
    },
  },
  business: {
    id: 'business',
    name: 'Business',
    limits: {
      rendersPerMonth: 25000,
      templates: null,
      members: 50,
      automations: null,
      maxBatchSize: 5000,
      apiRateLimitPerMinute: 300,
      maxScale: 3,
      storageMb: 50000,
      aiCredits: 2000,
      customSizes: null,
      brands: null,
      videoSecondsPerMonth: 30000,
    },
    features: {
      api: true,
      customFonts: true,
      linkedSizes: true,
      integrations: true,
      scheduling: true,
      signedUrls: true,
      qrStyles: true,
      versionHistory: true,
      comments: true,
      activityLog: true,
      approvals: true,
      pdf: true,
      browserRender: true,
      prioritySupport: true,
    },
  },
};

export function getPlan(id: string | null | undefined): Plan {
  return PLANS[(id as PlanId) ?? 'free'] ?? PLANS.free;
}

export function isPlanId(id: unknown): id is PlanId {
  return typeof id === 'string' && (PLAN_IDS as readonly string[]).includes(id);
}

/** Returns true if `used + adding` stays within `limit` (null = unlimited). */
export function withinLimit(limit: number | null, used: number, adding = 1): boolean {
  return limit === null || used + adding <= limit;
}
